import { Action, createReducer, on } from '@ngrx/store';
import {
    allTimesheetsRemoved,
    initTimesheet,
    removeSubmittedNotificationsFromCurrentTimesheet,
    timesheetCollectionSaved,
    timesheetCollectionStorageSyncError,
    timesheetEdited,
    timesheetEntryDeleted,
    timesheetInitialized,
    timesheetManualEntryAdded,
    timesheetRemoved,
    timesheetsChangedFromStorage,
    timesheetSelected,
    timesheetSubmitted,
    timesheetTravelBackEndAdjusted,
    updateStartOfWork,
} from '../actions/timesheet.actions';
import {
    createTimesheetState,
    initialTimesheetCollectionState,
    TimesheetCollectionState,
    TimesheetManualEntryState,
    TimesheetState,
} from '../timesheet-collection.state';
import { withSortedEntries } from '../utils/sorted-entries';
import { withinRetention } from '../utils/timesheet-retention';
import { withoutInProgressWorkEntries } from '../utils/in-progress-work-entries';
import { deriveWorkEntriesForConfirmation, replaceNotificationWorkEntries } from '../utils/derive-confirmation-entries';
import {
    submitConfirmationAborted,
    submitConfirmationRequested,
} from '../../confirmation/actions/confirmation.actions';
import { deleteAddressRequested } from '../../address/actions/address.actions';
import { ConfirmationState } from '../../confirmation/confirmation-collection.state';
import { ConfirmationTimesheetContext } from '../../utilities/confirmation-timesheet-context';
import { getUuidV4 } from '../../../utilities/generic-utilities';
import { TIMERECORDING_TYPE_OF_WORK_CODES } from '../../../models/complex/time-recording-entry.model';
import { TimesheetEntryPatch } from '../utils/timesheet-form-model';
import { isDefined } from '../../../utilities/is-defined';
import { LoadStatus } from '../../load-status';
import { NotificationIdState } from '../../utilities/notification-id.type';

/**
 * Applies an edit to one addressed, editable timesheet, and records the outcome for the persist
 * effect.
 *
 * Every UI-driven write shares this frame: the timesheet is addressed by id rather than being "the
 * current one", because the page can have an older timesheet open and an edit must land on exactly
 * the one that was edited; a submitted timesheet is refused, since the UI renders those read-only
 * and reaching here means a guard was bypassed. On success the entries are re-sorted and any
 * non-fatal notes are passed on as warnings for the effect to log.
 *
 * @param state the timesheet slice
 * @param props identifies the timesheet and correlates the outcome
 * @param props.timesheetId the timesheet to edit
 * @param props.correlationId correlates the recorded outcome with the requesting dispatch
 * @param reducerName names the reducer in the recorded error, so a failure says which write failed
 * @param edit produces the edited timesheet plus any warnings
 * @returns the slice with the edit applied, or with the failure recorded
 */
const editTimesheet = (
    state: Readonly<TimesheetCollectionState>,
    props: { timesheetId: string; correlationId: string },
    reducerName: string,
    edit: (target: TimesheetState) => { timesheet: TimesheetState; warnings: string[] },
): Readonly<TimesheetCollectionState> => {
    const failure = (error: string): Readonly<TimesheetCollectionState> => ({
        ...state,
        integration: { correlationId: props.correlationId, error, warnings: [] },
    });

    const index = state.timesheets.findIndex((candidate) => candidate.timesheetId === props.timesheetId);
    const target = state.timesheets[index];
    if (!isDefined(target)) {
        return failure(`Timesheet: ${reducerName}: no timesheet with id ${props.timesheetId}`);
    }
    if (target.isSubmitted) {
        return failure(`Timesheet: ${reducerName}: timesheet ${props.timesheetId} is already submitted`);
    }

    const { timesheet, warnings } = edit(target);
    return {
        ...state,
        timesheets: state.timesheets.toSpliced(index, 1, withSortedEntries(timesheet)),
        hasUnpersistedEdits: true,
        integration: { correlationId: props.correlationId, warnings },
    };
};

/**
 * Takes the timesheets storage holds, except the one being edited.
 *
 * This runs on init and again whenever a sync completes, which is what lets a change made on
 * another device reach the slice. That is worth having, but it must not cost the technician the
 * edits they are making right now: while a write is still on its way to storage, the stored copy
 * of the timesheet they are editing predates it, and taking it would silently undo their work.
 * So the unsubmitted timesheet stays as the store has it until a persist confirms; everything
 * else — the submitted timesheets, and any timesheet storage knows about and the store does
 * not — comes from storage either way.
 *
 * The narrowing is deliberate: only the unsubmitted timesheet is editable, so it is the only one
 * an edit can be in flight for.
 *
 * The retention window is applied here too, for the same reason the entry sort is: this is where
 * storage enters the slice, and it is the only place old timesheets can arrive. Trimming on the way
 * in also means the next persist writes the trimmed collection back, so storage follows without a
 * sweep of its own.
 *
 * Work entries of an unsubmitted confirmation are dropped for the same reason: they are derived from
 * the confirmation slice at read time, so a stored one would be rendered next to its derived twin.
 * See `withoutInProgressWorkEntries` for why this is a standing invariant rather than a migration.
 *
 * The timesheet being edited is taken from the slice rather than from storage, so it is deliberately
 * not pruned — it is live state, not something read back.
 *
 * @param state the timesheet slice
 * @param props the action payload
 * @param props.timesheets the timesheets read back from storage
 * @param props.now the current instant, against which the retention window is measured
 * @param props.retentionDays how many previous days to keep; 0 keeps everything
 * @returns the slice rehydrated from storage, trimmed, keeping an in-flight edit
 */
const setTimesheetsReducer = (
    state: Readonly<TimesheetCollectionState>,
    props: { timesheets: TimesheetState[]; now: string; retentionDays: number },
): Readonly<TimesheetCollectionState> => {
    // Storage is outside the invariant, so restore it on the way in rather than trusting it. Trim
    // first, so nothing is normalized that is about to be dropped. Pruning the in-progress work
    // entries also re-sorts, which is why there is no separate `withSortedEntries` pass.
    const stored = withoutInProgressWorkEntries(withinRetention(props.timesheets, props.now, props.retentionDays));
    const beingEdited = state.hasUnpersistedEdits
        ? state.timesheets.find((candidate) => candidate.isSubmitted === false)
        : undefined;

    if (!isDefined(beingEdited)) {
        return { ...state, timesheets: stored, status: LoadStatus.Loaded, error: null };
    }

    // Whichever timesheet storage holds as the open one gives way, by id or by being open itself:
    // exactly one timesheet may be unsubmitted, and it is the one carrying the edits.
    const others = stored.filter(
        (candidate) => candidate.timesheetId !== beingEdited.timesheetId && candidate.isSubmitted,
    );

    return {
        ...state,
        timesheets: [beingEdited, ...others],
        status: LoadStatus.Loaded,
        error: null,
    };
};

/**
 * Records that everything in the slice has reached storage.
 *
 * A persist writes the whole slice rather than the one edit that triggered it, so a single
 * success clears every outstanding write at once.
 *
 * @param state the timesheet slice
 * @returns the slice with no edits outstanding
 */
const setPersistedReducer = (state: Readonly<TimesheetCollectionState>): Readonly<TimesheetCollectionState> => ({
    ...state,
    hasUnpersistedEdits: false,
});

/**
 * Records which timesheet the UI shows. Unknown ids are stored as given — the selector
 * resolves them, falling back to the newest timesheet — so a rehydrate that drops the
 * selected timesheet needs no reducer-side cleanup.
 *
 * @param state the timesheet slice
 * @param props the action payload
 * @param props.timesheetId the timesheet to show
 * @returns the slice with the new selection
 */
const selectTimesheetReducer = (
    state: Readonly<TimesheetCollectionState>,
    props: { timesheetId: string },
): Readonly<TimesheetCollectionState> => ({
    ...state,
    selectedTimesheetId: props.timesheetId,
});

const setLoadingReducer = (state: Readonly<TimesheetCollectionState>): Readonly<TimesheetCollectionState> => ({
    ...state,
    status: LoadStatus.Loading,
});

const setLoadedReducer = (state: Readonly<TimesheetCollectionState>): Readonly<TimesheetCollectionState> => ({
    ...state,
    status: LoadStatus.Loaded,
});

const setErrorReducer = (
    state: Readonly<TimesheetCollectionState>,
    props: { error: string },
): Readonly<TimesheetCollectionState> => ({
    ...state,
    error: props.error,
});

/**
 * Moves the accepted current timesheet into the submitted slot and seeds the
 * new empty current one — the store is the source of truth for this swap. The
 * persist effect writes both slots to storage right after. The end of work the out model was
 * sent with is settled on the submitted timesheet: the form only shows an unset one without
 * writing it, and readers comparing against a submitted timesheet need the stored value. Without a current
 * timesheet there is nothing to submit — the persist effect reports that as the
 * failure outcome.
 *
 * @param state the timesheet slice
 * @param props the action payload
 * @param props.newTimesheetId identity of the empty timesheet replacing the submitted current one
 * @param props.endOfWork the end of work the submitted out model was built with
 * @returns the slice with the swapped timesheets
 */
const submitTimesheetReducer = (
    state: Readonly<TimesheetCollectionState>,
    props: { newTimesheetId: string; endOfWork: string },
): Readonly<TimesheetCollectionState> => {
    const currentTimesheetIndex = state.timesheets.findIndex((t) => t.isSubmitted === false);
    const currentTimesheet = state.timesheets[currentTimesheetIndex];

    return isDefined(currentTimesheet)
        ? {
              ...state,
              timesheets: [
                  createTimesheetState(props.newTimesheetId), // new current timesheet
                  ...state.timesheets.toSpliced(currentTimesheetIndex, 1, {
                      ...currentTimesheet,
                      endOfWork: props.endOfWork,
                      isSubmitted: true,
                  }),
              ],
              hasUnpersistedEdits: true,
          }
        : state; // error recovery
};

/**
 * Writes the new start of work into the current timesheet and records the
 * outcome for the persist effect, mirroring the notification-upsert write path.
 *
 * @param state the timesheet slice
 * @param props the action payload
 * @param props.startOfWork the new start of work as ISO timestamp
 * @param props.correlationId correlates the recorded outcome with the requesting dispatch
 * @returns the slice with the updated current timesheet and the outcome in `integration`
 */
const updateStartOfWorkReducer = (
    state: Readonly<TimesheetCollectionState>,
    props: { startOfWork: string; correlationId: string },
): Readonly<TimesheetCollectionState> => {
    const currentTimesheetIndex = state.timesheets.findIndex((t) => t.isSubmitted === false);
    const currentTimesheet = state.timesheets[currentTimesheetIndex];

    if (!isDefined(currentTimesheet)) {
        return {
            ...state,
            integration: {
                correlationId: props.correlationId,
                error: 'Timesheet: updateStartOfWorkReducer: currentTimesheet is undefined',
                warnings: [],
            },
        };
    }
    return {
        ...state,
        timesheets: state.timesheets.toSpliced(currentTimesheetIndex, 1, {
            ...state.timesheets[currentTimesheetIndex],
            startOfWork: props.startOfWork,
        }),
        // changing start-of-work makes the new timesheet the selected one by default
        selectedTimesheetId: currentTimesheet.timesheetId,
        hasUnpersistedEdits: true,
        integration: { correlationId: props.correlationId, warnings: [] },
    };
};

/**
 * Applies the timesheet form's committed draft: the new start and end of work, plus one patch per
 * entry merged in by `entryId`.
 *
 * Addressed by `timesheetId` rather than "the current timesheet", because the UI can have an older
 * one open — a commit must land on the timesheet that was edited, and on nothing else. A submitted
 * timesheet is refused: the UI renders it read-only, so a commit against one means the guard was
 * bypassed.
 *
 * Patches whose `entryId` is unknown are ignored rather than inserted. Adding an entry is a
 * structural change with its own action, so an unknown id here means the draft raced a rehydrate
 * that removed the entry; dropping the patch loses one edit, whereas inserting would resurrect a
 * deleted entry.
 *
 * @param state the timesheet slice
 * @param props the action payload
 * @param props.timesheetId the timesheet the draft belongs to
 * @param props.startOfWork the committed start of work as an ISO timestamp
 * @param props.endOfWork the committed end of work as an ISO timestamp
 * @param props.entries the per-entry patches to merge
 * @param props.correlationId correlates the recorded outcome with the requesting dispatch
 * @returns the slice with the draft applied and the outcome in `integration`
 */
const commitTimesheetEntriesReducer = (
    state: Readonly<TimesheetCollectionState>,
    props: {
        timesheetId: string;
        startOfWork: string;
        endOfWork: string;
        entries: TimesheetEntryPatch[];
        correlationId: string;
    },
): Readonly<TimesheetCollectionState> =>
    editTimesheet(state, props, 'commitTimesheetEntriesReducer', (target) => {
        const patchesByEntryId = new Map(props.entries.map((patch) => [patch.entryId, patch]));
        const warnings = props.entries
            .filter((patch) => !target.entries.some((entry) => entry.entryId === patch.entryId))
            .map(
                (patch) =>
                    `Timesheet: commitTimesheetEntriesReducer: dropped a patch for unknown entry ${patch.entryId}`,
            );

        const entries = target.entries.map((entry) => {
            const patch = patchesByEntryId.get(entry.entryId);
            if (!patch) {
                return entry;
            }
            const times = { start: patch.start, end: patch.end };
            return entry.kind === 'manual'
                ? {
                      ...entry,
                      ...times,
                      ...(patch.taskType === undefined ? {} : { taskType: patch.taskType }),
                      ...(patch.address === undefined ? {} : { address: patch.address }),
                  }
                : {
                      ...entry,
                      ...times,
                      // A travel-back leg can be switched between billable and standby, which is a
                      // change of work code; every other work entry round-trips its own value.
                      ...(patch.workCode === undefined ? {} : { workCode: patch.workCode }),
                      ...(patch.meansOfTransport === undefined ? {} : { meansOfTransport: patch.meansOfTransport }),
                  };
        });

        return {
            timesheet: { ...target, startOfWork: props.startOfWork, endOfWork: props.endOfWork, entries },
            warnings,
        };
    });

/**
 * Adds the manual entry that closes a gap, spanning exactly the time the gap covered.
 *
 * The same action serves the plain case and continuing a travel-back leg: both add one entry over
 * a span, and the travel case only carries the attributes the new leg inherits.
 *
 * @param state the timesheet slice
 * @param props the action payload
 * @param props.timesheetId the timesheet to add to
 * @param props.entryId identity of the new entry, minted at the dispatch site
 * @param props.start the new entry's start as an ISO timestamp
 * @param props.end the new entry's end as an ISO timestamp
 * @param props.notificationId the notification the entry is booked against
 * @param props.travelType set when the entry continues a travel-back leg, fixing its kind
 * @param props.isTravelBillable inherited from the leg being continued
 * @param props.correlationId correlates the recorded outcome with the requesting dispatch
 * @returns the slice with the new entry in place
 */
const addManualEntryReducer = (
    state: Readonly<TimesheetCollectionState>,
    props: {
        timesheetId: string;
        entryId: string;
        start: string;
        end: string;
        notificationId: NotificationIdState;
        travelType?: TIMERECORDING_TYPE_OF_WORK_CODES;
        isTravelBillable?: boolean;
        correlationId: string;
    },
): Readonly<TimesheetCollectionState> =>
    editTimesheet(state, props, 'addManualEntryReducer', (target) => {
        if (target.entries.some((entry) => entry.entryId === props.entryId)) {
            return { timesheet: target, warnings: [`Timesheet: addManualEntryReducer: entry ${props.entryId} exists`] };
        }

        const manualEntry: TimesheetManualEntryState = {
            kind: 'manual',
            entryId: props.entryId,
            notificationId: props.notificationId,
            start: props.start,
            end: props.end,
            // The user still has to choose both; the schema reports them as required until they do.
            address: null,
            taskType: '',
            addressInOutModel: true,
            isTravelBillable: props.isTravelBillable ?? false,
            // A leg continuing a travel-back inherits its kind, so there is nothing to choose.
            ...(props.travelType === undefined ? {} : { travelType: props.travelType }),
        };

        return { timesheet: { ...target, entries: [...target.entries, manualEntry] }, warnings: [] };
    });

/**
 * Removes an entry the user added by hand, reopening the gap it filled.
 *
 * Only manual entries may go: a work entry belongs to a confirmation, and deleting it here would
 * put the timesheet out of step with the confirmation that recorded it.
 *
 * @param state the timesheet slice
 * @param props the action payload
 * @param props.timesheetId the timesheet to delete from
 * @param props.entryId the entry to remove
 * @param props.correlationId correlates the recorded outcome with the requesting dispatch
 * @returns the slice without the entry
 */
const deleteEntryReducer = (
    state: Readonly<TimesheetCollectionState>,
    props: { timesheetId: string; entryId: string; correlationId: string },
): Readonly<TimesheetCollectionState> =>
    editTimesheet(state, props, 'deleteEntryReducer', (target) => {
        const doomed = target.entries.find((entry) => entry.entryId === props.entryId);
        if (!isDefined(doomed)) {
            return {
                timesheet: target,
                warnings: [`Timesheet: deleteEntryReducer: no entry ${props.entryId} to delete`],
            };
        }
        if (doomed.kind !== 'manual') {
            return {
                timesheet: target,
                warnings: [`Timesheet: deleteEntryReducer: refused to delete the work entry ${props.entryId}`],
            };
        }

        return {
            timesheet: { ...target, entries: target.entries.filter((entry) => entry.entryId !== props.entryId) },
            warnings: [],
        };
    });

/**
 * Commits a submitted confirmation's time recording as the current timesheet's work entries.
 *
 * Reduced from the confirmation's own submit action rather than from a timesheet-specific one: the
 * confirmation slice stores the snapshot on the same action, so both slices see exactly the same
 * confirmation, and the timesheet no longer has to be told separately by a service.
 *
 * The entries are derived by the function the read-time projection also uses, differing only in the
 * context: a submitted confirmation carries its real travel distance and its entries are committed
 * rather than in progress. Entry ids are minted here — a reducer may, where a selector may not.
 *
 * Idempotent by construction: the whole set of the notification's work entries is replaced, so a
 * retry after a failed submit overwrites its own earlier attempt instead of adding to it.
 *
 * @param state the timesheet slice
 * @param props the action payload
 * @param props.confirmation the confirmation being submitted
 * @param props.timesheet the surroundings the derivation needs, resolved at the dispatch site
 * @param props.correlationId correlates the recorded outcome with the requesting dispatch
 * @returns the slice with the confirmation's work entries committed to `current`
 */
const commitConfirmationEntriesReducer = (
    state: Readonly<TimesheetCollectionState>,
    props: { confirmation: ConfirmationState; timesheet: ConfirmationTimesheetContext; correlationId: string },
): Readonly<TimesheetCollectionState> => {
    const currentTimesheetIndex = state.timesheets.findIndex((t) => t.isSubmitted === false);
    const currentTimesheet = state.timesheets[currentTimesheetIndex];

    if (!isDefined(currentTimesheet)) {
        return {
            ...state,
            integration: {
                correlationId: props.correlationId,
                error: 'Timesheet: commitConfirmationEntriesReducer: currentTimesheet is undefined',
                warnings: [],
            },
        };
    }

    const { timeRecording, notificationId } = props.confirmation;
    if (!isDefined(timeRecording)) {
        // A submitted confirmation always has this section; recording the miss rather than throwing,
        // because a throw here would take the store down with it.
        return {
            ...state,
            integration: {
                correlationId: props.correlationId,
                error: 'Timesheet: commitConfirmationEntriesReducer: confirmation has no time recording section',
                warnings: [],
            },
        };
    }

    const entries = deriveWorkEntriesForConfirmation(
        timeRecording,
        notificationId,
        props.timesheet.address ?? undefined,
        {
            customerName: props.timesheet.customerName,
            notificationType: props.timesheet.notificationType,
            confirmationType: props.confirmation.confirmationType,
            // The confirmation's own distance, which only a submitted confirmation carries.
            distance: parseFloat(props.confirmation.expenses?.distance ?? '') || 0,
            isConfirmationSubmitted: true,
            flags: props.timesheet.flags,
        },
        () => getUuidV4(),
    );

    return {
        ...state,
        timesheets: state.timesheets.toSpliced(
            currentTimesheetIndex,
            1,
            replaceNotificationWorkEntries(currentTimesheet, notificationId, entries),
        ),
        hasUnpersistedEdits: true,
        integration: { correlationId: props.correlationId, warnings: [] },
    };
};

/**
 * Takes back the work entries a submit committed, when that submit turned out to send nothing.
 *
 * An aborted submit means the notification was already completed elsewhere, so nothing reached SAP —
 * but the commit already ran, and the cleanup that follows removes the confirmation from its slice,
 * which is what would otherwise keep the derived entries visible in its place. Without this the
 * timesheet would keep entries for work it never submitted.
 *
 * A *failed* submit needs no counterpart: the confirmation is left in place to be retried, so the
 * read-time projection still covers it and a retry replaces these entries outright.
 *
 * @param state the timesheet slice
 * @param props the action payload
 * @param props.notificationId the notification whose committed entries are being taken back
 * @param props.correlationId correlates the recorded outcome with the requesting dispatch
 * @returns the slice without the notification's committed work entries
 */
const dropCommittedConfirmationEntriesReducer = (
    state: Readonly<TimesheetCollectionState>,
    props: { notificationId: NotificationIdState; correlationId: string },
): Readonly<TimesheetCollectionState> => {
    const currentTimesheetIndex = state.timesheets.findIndex((t) => t.isSubmitted === false);
    const currentTimesheet = state.timesheets[currentTimesheetIndex];

    if (!isDefined(currentTimesheet)) {
        return state;
    }

    return {
        ...state,
        timesheets: state.timesheets.toSpliced(
            currentTimesheetIndex,
            1,
            replaceNotificationWorkEntries(currentTimesheet, props.notificationId, []),
        ),
        hasUnpersistedEdits: true,
        integration: { correlationId: props.correlationId, warnings: [] },
    };
};

/**
 * Drops the work entries of the already-submitted confirmations from the current timesheet and
 * records the outcome for the persist effect, mirroring the start-of-work write path.
 *
 * Only work entries go: manual entries carry a notification id too (the "add travel back" ones),
 * but they belong to the user rather than to a confirmation — the same split
 * {@link replaceNotificationWorkEntries} makes.
 *
 * @param state the timesheet slice
 * @param props the action payload
 * @param props.correlationId correlates the recorded outcome with the requesting dispatch
 * @returns the slice with the submitted confirmations' entries removed from the current timesheet
 */
const removeSubmittedNotificationsReducer = (
    state: Readonly<TimesheetCollectionState>,
    props: { correlationId: string },
): Readonly<TimesheetCollectionState> => {
    const currentTimesheetIndex = state.timesheets.findIndex((t) => t.isSubmitted === false);
    const currentTimesheet = state.timesheets[currentTimesheetIndex];

    if (!isDefined(currentTimesheet)) {
        return {
            ...state,
            integration: {
                correlationId: props.correlationId,
                error: 'Timesheet: removeSubmittedNotificationsReducer: currentTimesheet is undefined',
                warnings: [],
            },
        };
    }
    return {
        ...state,
        timesheets: state.timesheets.toSpliced(currentTimesheetIndex, 1, {
            ...currentTimesheet,
            entries: currentTimesheet.entries.filter(
                (entry) => entry.kind !== 'work' || !entry.isConfirmationSubmitted,
            ),
        }),
        hasUnpersistedEdits: true,
        integration: { correlationId: props.correlationId, warnings: [] },
    };
};

/**
 * Takes a deleted address off every entry of the current timesheet that still points at it.
 *
 * The address slice tombstones a deleted address rather than dropping it — a submitted timesheet's
 * entry has to keep resolving it (see `deleteAddressRequested`) — so without this the entry the
 * technician just took the address away from would go on showing it: the row resolves its id
 * through `selectAddressesById`, which keeps tombstoned addresses. Clearing the id here is what
 * makes the row read as "no address picked" again, and what makes `addressRequired` ask for a new
 * one before the timesheet can be sent.
 *
 * Only the current timesheet is touched. A submitted one is history and is rendered read-only; its
 * entries keep the address they were sent with, which the tombstone keeps resolvable for the legacy
 * model bridge and the submit out model.
 *
 * Every entry kind is checked, though in practice only a manual entry can be affected: a work
 * entry's address is the notification's customer address, which is never user-created and so never
 * deletable (`deleteAddressReducer`).
 *
 * The slice reduces the address slice's own event rather than being commanded by a timesheet
 * action, so the two collections change in one transition — an entry can never be left pointing at
 * an address the address slice has already tombstoned. An address no entry uses leaves the slice
 * untouched, `integration` included: that is what tells `persistCurrentTimesheetAfterAddressDelete$`
 * there is nothing to write.
 *
 * @param state the timesheet slice
 * @param props the action payload
 * @param props.id the id of the address that was deleted
 * @param props.correlationId correlates the recorded outcome with the requesting dispatch
 * @returns the slice with the address taken off the current timesheet's entries
 */
export function timesheetAddressDeletedReducer(
    state: Readonly<TimesheetCollectionState>,
    props: { id: string; correlationId: string },
): Readonly<TimesheetCollectionState> {
    const currentTimesheetIndex = state.timesheets.findIndex((candidate) => candidate.isSubmitted === false);
    const currentTimesheet = state.timesheets[currentTimesheetIndex];

    if (!isDefined(currentTimesheet) || !currentTimesheet.entries.some((entry) => entry.address === props.id)) {
        return state;
    }

    return {
        ...state,
        timesheets: state.timesheets.toSpliced(currentTimesheetIndex, 1, {
            ...currentTimesheet,
            entries: currentTimesheet.entries.map((entry) =>
                entry.address === props.id ? { ...entry, address: null } : entry,
            ),
        }),
        hasUnpersistedEdits: true,
        integration: { correlationId: props.correlationId, warnings: [] },
    };
}

/**
 * Discards one timesheet outright, for pace-spector's local-state cleanup.
 *
 * The "exactly one open timesheet" invariant means the current (unsubmitted) timesheet cannot
 * simply be dropped: a fresh empty one is reseeded under a newly minted id in its place, the same
 * way a submit swap seeds its replacement. A submitted timesheet has no such invariant to protect
 * and is just removed.
 *
 * @param state the timesheet slice
 * @param props the action payload
 * @param props.timesheetId the timesheet to discard
 * @param props.correlationId correlates the recorded outcome with the requesting dispatch
 * @returns the slice without the addressed timesheet
 */
const removeTimesheetReducer = (
    state: Readonly<TimesheetCollectionState>,
    props: { timesheetId: string; correlationId: string },
): Readonly<TimesheetCollectionState> => {
    const index = state.timesheets.findIndex((candidate) => candidate.timesheetId === props.timesheetId);
    const target = state.timesheets[index];
    if (!isDefined(target)) {
        return {
            ...state,
            integration: {
                correlationId: props.correlationId,
                error: `Timesheet: removeTimesheetReducer: no timesheet with id ${props.timesheetId}`,
                warnings: [],
            },
        };
    }

    const timesheets = target.isSubmitted
        ? state.timesheets.toSpliced(index, 1)
        : state.timesheets.toSpliced(index, 1, createTimesheetState(getUuidV4()));

    return {
        ...state,
        timesheets,
        hasUnpersistedEdits: true,
        integration: { correlationId: props.correlationId, warnings: [] },
    };
};

/**
 * Shortens a travel-back entry's end to `end` — the reducer half of the travel-status-update
 * effect's "adapt last TRBK time" step. Mirrors the legacy code's
 * `lastTravelBackEntry.endDate.value = ...; lastTravelBackEntry.endTime.value = ...`, which wrote
 * the same instant to both fields; the slice holds that instant once, in `end`.
 *
 * An exported named handler (rather than the file's usual private `const`) per the project's
 * reducer-handler convention: every `on()` handler added going forward is an exported named
 * function with its own JSDoc.
 *
 * @param state the timesheet slice
 * @param props the action payload
 * @param props.timesheetId the timesheet the leg belongs to
 * @param props.entryId the travel-back entry being shortened
 * @param props.end the new end, as an ISO timestamp
 * @param props.correlationId correlates the recorded outcome with the requesting dispatch
 * @returns the slice with the leg's new end, or with the failure recorded
 */
export function timesheetTravelBackEndAdjustedReducer(
    state: Readonly<TimesheetCollectionState>,
    props: { timesheetId: string; entryId: string; end: string; correlationId: string },
): Readonly<TimesheetCollectionState> {
    return editTimesheet(state, props, 'timesheetTravelBackEndAdjustedReducer', (target) => {
        const doomed = target.entries.find((entry) => entry.entryId === props.entryId);
        if (!isDefined(doomed)) {
            return {
                timesheet: target,
                warnings: [`Timesheet: timesheetTravelBackEndAdjustedReducer: no entry ${props.entryId} to adjust`],
            };
        }

        return {
            timesheet: {
                ...target,
                entries: target.entries.map((entry) =>
                    entry.entryId === props.entryId ? { ...entry, end: props.end } : entry,
                ),
            },
            warnings: [],
        };
    });
}

/**
 * Discards every timesheet the slice holds, for pace-spector's local-state cleanup, replacing
 * them with one fresh empty current timesheet so the "exactly one open timesheet" invariant
 * still holds afterwards.
 *
 * @param state the timesheet slice
 * @param props the action payload
 * @param props.correlationId correlates the recorded outcome with the requesting dispatch
 * @returns the slice holding only a fresh empty timesheet
 */
const removeAllTimesheetsReducer = (
    state: Readonly<TimesheetCollectionState>,
    props: { correlationId: string },
): Readonly<TimesheetCollectionState> => ({
    ...state,
    timesheets: [createTimesheetState(getUuidV4())],
    hasUnpersistedEdits: true,
    integration: { correlationId: props.correlationId, warnings: [] },
});

const createTimesheetCollectionReducer = createReducer(
    initialTimesheetCollectionState,
    on(initTimesheet, setLoadingReducer),
    on(timesheetsChangedFromStorage, setTimesheetsReducer),
    on(timesheetCollectionSaved, setPersistedReducer),
    on(timesheetEdited, commitTimesheetEntriesReducer),
    on(timesheetManualEntryAdded, addManualEntryReducer),
    on(timesheetEntryDeleted, deleteEntryReducer),
    on(updateStartOfWork, updateStartOfWorkReducer),
    on(submitConfirmationRequested, commitConfirmationEntriesReducer),
    on(submitConfirmationAborted, dropCommittedConfirmationEntriesReducer),
    on(removeSubmittedNotificationsFromCurrentTimesheet, removeSubmittedNotificationsReducer),
    on(deleteAddressRequested, timesheetAddressDeletedReducer),
    on(timesheetTravelBackEndAdjusted, timesheetTravelBackEndAdjustedReducer),
    on(timesheetSelected, selectTimesheetReducer),
    on(timesheetSubmitted, submitTimesheetReducer),
    on(timesheetRemoved, removeTimesheetReducer),
    on(allTimesheetsRemoved, removeAllTimesheetsReducer),
    on(timesheetInitialized, setLoadedReducer),
    on(timesheetCollectionStorageSyncError, setErrorReducer),
);

export function timesheetCollectionReducer(
    state: Readonly<TimesheetCollectionState>,
    action: Action,
): Readonly<TimesheetCollectionState> {
    return createTimesheetCollectionReducer(state, action);
}
